
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, FileSearch, Bug } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEstimateAnalysis } from "@/hooks/useEstimateAnalysis";
import { AdasAnalysisDebugger } from "./AdasAnalysisDebugger";

interface EstimateAnalysisProps {
  vin: string;
  estimateText: string;
  adasFeatures: any;
  disabled?: boolean;
}

export function EstimateAnalysis({ vin, estimateText, adasFeatures, disabled = false }: EstimateAnalysisProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showDebugger, setShowDebugger] = useState(false);
  const { analyzeEstimate, isAnalyzing, analysisResult, debugData, clearAnalysis } = useEstimateAnalysis();

  const handleAnalyze = async () => {
    if (!estimateText) {
      return;
    }
    
    setIsOpen(true);
    
    if (!analysisResult && !isAnalyzing) {
      await analyzeEstimate(estimateText, adasFeatures);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Don't clear the analysis result when closing, so it's still available if reopened
  };

  const handleRetry = async () => {
    await analyzeEstimate(estimateText, adasFeatures);
  };

  // Format OpenAI response for display
  const formatAnalysisContent = (result: any) => {
    if (!result) return null;
    
    try {
      // Extract text content from the OpenAI response
      const message = result.output?.find((item: any) => item.type === "message");
      if (!message) return "No analysis content available.";
      
      const textContent = message.content?.find((item: any) => item.type === "output_text");
      if (!textContent) return "No text analysis available.";
      
      return textContent.text;
    } catch (error) {
      console.error("Error formatting analysis:", error);
      return "Error displaying analysis results.";
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleAnalyze}
        disabled={disabled || isAnalyzing || !estimateText}
        className="flex items-center gap-2"
      >
        {isAnalyzing ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <FileSearch className="h-4 w-4" />
        )}
        ADAS Analysis
      </Button>
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>ADAS Analysis Results</DialogTitle>
                <DialogDescription>
                  Analysis of estimate and ADAS features for VIN: {vin}
                </DialogDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDebugger(!showDebugger)}
              >
                <Bug className="h-4 w-4 mr-2" />
                {showDebugger ? "Hide Debugger" : "Show Debugger"}
              </Button>
            </div>
          </DialogHeader>
          
          <div className="space-y-4">
            {isAnalyzing ? (
              <div className="py-8 flex flex-col items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin mb-4" />
                <p className="text-muted-foreground">Analyzing ADAS components and calibration requirements...</p>
              </div>
            ) : analysisResult ? (
              <ScrollArea className="h-[500px] rounded-md border p-4">
                <div className="prose dark:prose-invert max-w-none">
                  <div className="whitespace-pre-wrap">
                    {formatAnalysisContent(analysisResult)}
                  </div>
                </div>
              </ScrollArea>
            ) : (
              <div className="py-8 text-center">
                <p className="text-muted-foreground">No analysis results available.</p>
                <Button 
                  onClick={handleRetry}
                  className="mt-4"
                >
                  Run Analysis
                </Button>
              </div>
            )}
            
            {showDebugger && debugData && (
              <AdasAnalysisDebugger
                isLoading={isAnalyzing}
                requestData={debugData.requestData}
                responseData={debugData.responseData}
                onRetry={handleRetry}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
